import React, { useEffect, useState } from "react";
import api, { API, formatApiErrorDetail, getAuthToken } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, FileText, CheckCircle2, Ban, Truck } from "lucide-react";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_STYLE = {
    draft: "bg-stone-100 border-stone-200 text-stone-600",
    pending_approval: "bg-amber-50 border-amber-200 text-amber-800",
    awarded: "bg-green-50 border-green-200 text-green-700",
    delivered: "bg-blue-50 border-blue-200 text-blue-700",
    cancelled: "bg-red-50 border-red-200 text-red-700",
};

export default function Orders() {
    const { hasPerm, isApprover } = useAuth();
    const [items, setItems] = useState([]);
    const [projects, setProjects] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [show, setShow] = useState(false);
    const [showGrn, setShowGrn] = useState(null);
    const [form, setForm] = useState({ project_id: "", vendor_id: "", mode: "single", notes: "", lines: [] });
    const [err, setErr] = useState("");

    const load = async () => {
        const [o, p, v] = await Promise.all([api.get("/orders"), api.get("/projects"), api.get("/vendors")]);
        setItems(o.data); setProjects(p.data); setVendors(v.data);
    };
    useEffect(() => { load(); }, []);

    const addLine = () => setForm({ ...form, lines: [...form.lines, { part_code: "", description: "", hsn_code: "", qty: 0, rate: 0, gst_rate: 18, vendor_id: "" }] });
    const setLine = (i, p) => { const a = [...form.lines]; a[i] = { ...a[i], ...p }; setForm({ ...form, lines: a }); };
    const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) });

    const submit = async (e) => {
        e.preventDefault(); setErr("");
        try {
            await api.post("/orders", form);
            setShow(false); setForm({ project_id: "", vendor_id: "", mode: "single", notes: "", lines: [] }); load();
        } catch (err) { setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    };
    const approve = async (o, approve = true) => {
        const reason = o.budget_exceeded ? prompt("Order exceeds budget. Please enter override reason:") : "";
        if (o.budget_exceeded && reason == null) return;
        try { await api.post(`/orders/${o.id}/approve`, { approve, override_budget_reason: reason || "" }); load(); }
        catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    const downloadPdf = (o) => {
        const tok = getAuthToken();
        const url = `${API}/orders/${o.id}/pdf${tok ? `?_=${Date.now()}` : ""}`;
        // Use fetch to include cookie / bearer, then create object URL
        fetch(url, { credentials: "include", headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
            .then(r => r.blob()).then(b => {
                const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${o.po_number}.pdf`; a.click();
            });
    };

    return (
        <div data-testid="orders-page" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Operations</div>
                    <h1 className="text-3xl font-heading font-semibold text-stone-900">Orders</h1>
                </div>
                {hasPerm("order.create") && <button data-testid="new-order-btn" onClick={() => setShow(true)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2"><Plus size={15} /> New order</button>}
            </div>

            <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                            <th className="text-left py-3 px-4">PO #</th>
                            <th className="text-left py-3 px-4">Project</th>
                            <th className="text-left py-3 px-4">Vendor</th>
                            <th className="text-right py-3 px-4">Total</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Budget</th>
                            <th className="text-right py-3 px-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(o => (
                            <tr key={o.id} className="border-t border-stone-100 hover:bg-stone-50">
                                <td className="py-3 px-4 font-mono text-xs text-stone-800">{o.po_number}</td>
                                <td className="py-3 px-4 text-stone-700">{o.project_name}</td>
                                <td className="py-3 px-4 text-stone-700">{o.vendor_name || (o.mode === "split" ? "Split" : "—")}</td>
                                <td className="py-3 px-4 text-right font-medium">{inr(o.total)}</td>
                                <td className="py-3 px-4"><span className={"text-xs px-2 py-0.5 rounded-md border capitalize " + (STATUS_STYLE[o.status] || "bg-stone-100 border-stone-200 text-stone-600")}>{o.status?.replace("_", " ")}</span></td>
                                <td className="py-3 px-4">
                                    {o.budget_exceeded ? <span className="text-xs bg-red-50 border border-red-200 text-red-700 rounded-md px-2 py-0.5">Exceeded</span> : <span className="text-xs text-stone-400">OK</span>}
                                </td>
                                <td className="py-3 px-4 text-right space-x-3 whitespace-nowrap">
                                    {o.status === "pending_approval" && isApprover() && (
                                        <>
                                            <button data-testid={`approve-${o.id}`} onClick={() => approve(o, true)} className="text-xs text-green-700 hover:text-green-900 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Award</button>
                                            <button onClick={() => approve(o, false)} className="text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1"><Ban size={12} /> Reject</button>
                                        </>
                                    )}
                                    {(o.status === "awarded") && (
                                        <button data-testid={`grn-${o.id}`} onClick={() => setShowGrn(o)} className="text-xs text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"><Truck size={12} /> GRN</button>
                                    )}
                                    <button onClick={() => downloadPdf(o)} className="text-xs text-stone-700 hover:text-stone-900 inline-flex items-center gap-1"><FileText size={12} /> PDF</button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-stone-500 text-sm">No orders yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            {show && (
                <OrderModal projects={projects} vendors={vendors} form={form} setForm={setForm} err={err} submit={submit} onClose={() => setShow(false)} addLine={addLine} setLine={setLine} rmLine={rmLine} />
            )}
            {showGrn && (
                <GrnModal order={showGrn} onClose={() => setShowGrn(null)} onSaved={() => { setShowGrn(null); load(); }} />
            )}
        </div>
    );
}

function OrderModal({ projects, vendors, form, setForm, err, submit, onClose, addLine, setLine, rmLine }) {
    const total = form.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.rate) || 0), 0);
    const tax = form.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.rate) || 0) * (Number(l.gst_rate) || 0) / 100, 0);
    const [projectQuotes, setProjectQuotes] = React.useState([]);
    const [pickQuotationId, setPickQuotationId] = React.useState("");

    React.useEffect(() => {
        if (!form.project_id) { setProjectQuotes([]); setPickQuotationId(""); return; }
        api.get(`/projects/${form.project_id}/quotations?latest_only=true`).then(r => setProjectQuotes(r.data)).catch(() => setProjectQuotes([]));
    }, [form.project_id]);

    const copyFromQuotation = (qid) => {
        setPickQuotationId(qid);
        const q = projectQuotes.find(x => x.id === qid);
        if (!q) return;
        // Copy lines and default to that vendor for single-mode orders
        setForm(prev => ({
            ...prev,
            vendor_id: prev.mode === "single" ? q.vendor_id : prev.vendor_id,
            lines: (q.lines || []).map(l => ({
                part_code: l.part_code || "", description: l.description || "", hsn_code: l.hsn_code || "",
                qty: l.qty || 0, rate: l.rate || 0, gst_rate: l.gst_rate || 18,
                vendor_id: prev.mode === "split" ? q.vendor_id : "",
            }))
        }));
    };

    return (
        <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
            <div className="bg-white border border-stone-200 rounded-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
                    <div className="font-heading font-semibold text-stone-900">New order</div>
                    <button onClick={onClose} className="text-stone-500"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-4">
                    {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                    <div className="grid md:grid-cols-3 gap-4">
                        <div>
                            <label className="overline block mb-1">Project *</label>
                            <select data-testid="o-project" required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                                <option value="">Select project</option>
                                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="overline block mb-1">Mode</label>
                            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                                <option value="single">Single vendor</option>
                                <option value="split">Split (per line)</option>
                            </select>
                        </div>
                        {form.mode === "single" && (
                            <div>
                                <label className="overline block mb-1">Vendor *</label>
                                <select data-testid="o-vendor" required value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                                    <option value="">Select vendor</option>
                                    {vendors.filter(v => v.status !== "blacklisted").map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    {form.project_id && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                            <label className="overline block mb-1 text-amber-900">Copy lines from a quotation (recommended)</label>
                            <select data-testid="o-copy-quotation" value={pickQuotationId} onChange={(e) => copyFromQuotation(e.target.value)}
                                    className="w-full border border-amber-300 rounded-md text-sm py-2 px-3 bg-white">
                                <option value="">— Pick a quotation to auto-populate lines —</option>
                                {projectQuotes.map(q => (
                                    <option key={q.id} value={q.id}>
                                        {q.vendor_name} · v{q.version || 1} · ₹ {Number(q.total || 0).toLocaleString("en-IN")} ({q.lines?.length || 0} lines)
                                    </option>
                                ))}
                            </select>
                            <div className="text-[11px] text-stone-600 mt-1">Only latest version per vendor is shown. Remove any line below if you want to split.</div>
                        </div>
                    )}

                    <div className="border border-stone-200 rounded-xl overflow-x-auto">
                        <table className="w-full text-sm min-w-[900px]">
                            <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                <tr>
                                    <th className="text-left py-2.5 px-3">Part code</th>
                                    <th className="text-left py-2.5 px-3">Description</th>
                                    <th className="text-left py-2.5 px-3">HSN</th>
                                    <th className="text-right py-2.5 px-3">Qty</th>
                                    <th className="text-right py-2.5 px-3">Rate</th>
                                    <th className="text-right py-2.5 px-3">GST %</th>
                                    {form.mode === "split" && <th className="text-left py-2.5 px-3">Vendor</th>}
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {form.lines.map((l, i) => (
                                    <tr key={i} className="border-t border-stone-100">
                                        <td className="p-1"><input value={l.part_code} onChange={(e) => setLine(i, { part_code: e.target.value })} className="w-32 border border-stone-200 rounded-md text-sm py-1.5 px-2 font-mono" /></td>
                                        <td className="p-1"><input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} className="w-full border border-stone-200 rounded-md text-sm py-1.5 px-2" /></td>
                                        <td className="p-1"><input value={l.hsn_code} onChange={(e) => setLine(i, { hsn_code: e.target.value })} className="w-24 border border-stone-200 rounded-md text-sm py-1.5 px-2 font-mono" /></td>
                                        <td className="p-1"><input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="w-20 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                        <td className="p-1"><input type="number" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} className="w-24 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                        <td className="p-1"><input type="number" value={l.gst_rate} onChange={(e) => setLine(i, { gst_rate: e.target.value })} className="w-16 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                        {form.mode === "split" && (
                                            <td className="p-1">
                                                <select value={l.vendor_id} onChange={(e) => setLine(i, { vendor_id: e.target.value })} className="w-40 border border-stone-200 rounded-md text-sm py-1.5 px-2">
                                                    <option value="">—</option>
                                                    {vendors.filter(v => v.status !== "blacklisted").map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                                </select>
                                            </td>
                                        )}
                                        <td className="p-1"><button type="button" onClick={() => rmLine(i)} className="text-stone-400 hover:text-red-700">×</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <button type="button" onClick={addLine} className="text-sm text-amber-700 inline-flex items-center gap-1"><Plus size={13} /> Add blank line</button>

                    <div className="flex justify-end gap-8 text-sm">
                        <div>Subtotal: <b>{inr(total)}</b></div>
                        <div>GST: <b>{inr(tax)}</b></div>
                        <div>Total: <b className="text-amber-700">{inr(total + tax)}</b></div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose} className="text-sm text-stone-600 px-4 py-2 rounded-md">Cancel</button>
                        <button data-testid="save-order" type="submit" className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium">Submit for approval</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function GrnModal({ order, onClose, onSaved }) {
    const [lines, setLines] = useState(() => (order.lines || []).map(l => ({
        part_code: l.part_code, description: l.description,
        qty_ordered: l.qty, qty_received: l.qty, qty_shortage: 0, qty_damaged: 0, condition: "ok"
    })));
    const [d, setD] = useState(5), [q, setQ] = useState(5), [r, setR] = useState(5), [notes, setNotes] = useState(""), [err, setErr] = useState("");
    const setLine = (i, patch) => { const a = [...lines]; a[i] = { ...a[i], ...patch }; setLines(a); };

    const submit = async (e) => {
        e.preventDefault(); setErr("");
        try {
            await api.post("/grn", { order_id: order.id, lines, delivery_time_rating: Number(d), quality_rating: Number(q), reliability_rating: Number(r), notes });
            onSaved();
        } catch (err) { setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
            <div className="bg-white border border-stone-200 rounded-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
                    <div>
                        <div className="font-heading font-semibold text-stone-900">GRN for {order.po_number}</div>
                        <div className="text-xs text-stone-500">Record delivered qty, shortages, and vendor rating.</div>
                    </div>
                    <button onClick={onClose} className="text-stone-500"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-4">
                    {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                    <div className="border border-stone-200 rounded-xl overflow-x-auto">
                        <table className="w-full text-sm min-w-[700px]">
                            <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                <tr>
                                    <th className="text-left py-2 px-3">Part</th>
                                    <th className="text-right py-2 px-3">Ordered</th>
                                    <th className="text-right py-2 px-3">Received</th>
                                    <th className="text-right py-2 px-3">Shortage</th>
                                    <th className="text-right py-2 px-3">Damaged</th>
                                    <th className="text-left py-2 px-3">Condition</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((l, i) => (
                                    <tr key={i} className="border-t border-stone-100">
                                        <td className="p-1 text-xs"><div className="font-mono">{l.part_code}</div><div className="text-stone-500">{l.description}</div></td>
                                        <td className="p-1 text-right">{l.qty_ordered}</td>
                                        <td className="p-1"><input type="number" min="0" value={l.qty_received} onChange={(e) => setLine(i, { qty_received: Number(e.target.value) })} className="w-20 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                        <td className="p-1"><input type="number" min="0" value={l.qty_shortage} onChange={(e) => setLine(i, { qty_shortage: Number(e.target.value) })} className="w-20 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                        <td className="p-1"><input type="number" min="0" value={l.qty_damaged} onChange={(e) => setLine(i, { qty_damaged: Number(e.target.value) })} className="w-20 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                        <td className="p-1">
                                            <select value={l.condition} onChange={(e) => setLine(i, { condition: e.target.value })} className="border border-stone-200 rounded-md text-sm py-1.5 px-2">
                                                <option value="ok">OK</option>
                                                <option value="partial">Partial</option>
                                                <option value="damaged">Damaged</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                        <Rating label="Delivery time" value={d} onChange={setD} />
                        <Rating label="Quality" value={q} onChange={setQ} />
                        <Rating label="Reliability" value={r} onChange={setR} />
                    </div>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes" className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose} className="text-sm text-stone-600 px-4 py-2 rounded-md">Cancel</button>
                        <button data-testid="save-grn" type="submit" className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium">Submit GRN</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Rating({ label, value, onChange }) {
    return (
        <div>
            <label className="overline block mb-1">{label}</label>
            <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" onClick={() => onChange(n)} className={"w-8 h-8 rounded-md border text-sm " + (n <= value ? "bg-amber-500 text-white border-amber-500" : "bg-white border-stone-300 text-stone-500")}>{n}</button>
                ))}
            </div>
        </div>
    );
}
